
































































































































import Component, { mixins } from "vue-class-component";
import { Inject, Prop, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";

import FormField from "@/lib/components/FormField.vue";
import FormErrors from "@/lib/components/FormErrors.vue";
import LoadingSpinner from "@/lib/components/LoadingSpinner.vue";
import SubmitButton from "@/lib/components/SubmitButton.vue";

import GeneralFormMixin from "@/lib/mixins/GeneralForm";
import IdGenerator from "@/lib/services/id-generator";
import AuthenticatedUserProvider from "@/modules/account/authenticated-user-provider.service";
import { Resource } from "@/modules/account/resource";
import dataStore from "@/store";
import PlushieStore from "@/modules/plushie/store";
import Product from "@/modules/plushie/product.model";

import FactoryStore from "../../store";
import FactoryProductDepositSetting from "../../factory-product-deposit-setting.model";

interface DepositRow {
  key: string;
  productId: string;
  percent: string;
  isSaved: boolean;
}

@Component({
  components: { FormField, FormErrors, LoadingSpinner, SubmitButton },
})
export default class ProductDeposits extends mixins(GeneralFormMixin) {
  @Prop({ required: true })
  public readonly factoryId!: string;

  @Prop({ required: true })
  public readonly hasGeneralSettings!: boolean;

  @Inject("IdGenerator")
  private fIdGenerator!: IdGenerator;

  @Inject("AuthenticatedUserProvider")
  private fUserProvider!: AuthenticatedUserProvider;

  public rows: DepositRow[] = [];
  public showSaved = false;
  public isLoading = true;
  public loadErrors: string[] = [];
  public percentRules = {
    required: true,
    regex: /^\d+(?:\.\d{1,2})?$/,
    ["max_value"]: 100,
  };

  private fFactoryDataStore = getModule(FactoryStore, dataStore);
  private fPlushieDataStore = getModule(PlushieStore, dataStore);
  private fConfirmedRows: DepositRow[] = [];
  private fSavedTimer?: ReturnType<typeof setTimeout>;

  get canManage(): boolean {
    const user = this.fUserProvider.getUser();
    return (
      !!user &&
      user.hasPermissionForResource(Resource.FACTORY_INVOICE_SETTINGS_MANAGE)
    );
  }

  get hasChanges(): boolean {
    return (
      this.rows.length !== this.fConfirmedRows.length ||
      this.rows.some((row) => this.isRowChanged(row))
    );
  }

  public isRowChanged(row: DepositRow): boolean {
    if (!row.isSaved) {
      return true;
    }
    const original = this.fConfirmedRows.find((item) => item.key === row.key);
    return !original || original.percent !== row.percent;
  }

  get eligibleProducts(): Product[] {
    return this.fPlushieDataStore.activeProductsList
      .filter((product) => !product.isBatch)
      .sort((first, second) => this.compareProducts(first.id, second.id));
  }

  get availableProducts(): Product[] {
    return this.eligibleProducts.filter(
      (product) => !this.rows.some((row) => row.productId === product.id)
    );
  }

  public optionsFor(row: DepositRow): Product[] {
    return this.eligibleProducts.filter(
      (product) =>
        !this.rows.some(
          (other) => other !== row && other.productId === product.id
        )
    );
  }

  public productLabel(productId: string): string {
    const product = this.fPlushieDataStore.getProductById(productId);
    return product ? product.name : productId;
  }

  public addRow(): void {
    if (this.isDisabled || !this.canManage || !this.availableProducts.length) {
      return;
    }
    this.rows.push({
      key: this.fIdGenerator.getId(),
      productId: "",
      percent: "",
      isSaved: false,
    });
  }

  public removeRow(row: DepositRow): void {
    if (this.isDisabled || !this.canManage) {
      return;
    }
    this.rows.splice(this.rows.indexOf(row), 1);
  }

  public reset(): void {
    if (this.isDisabled || !this.canManage) {
      return;
    }
    this.rebuildRows(
      this.fFactoryDataStore.getProductDepositSettingsByFactoryId(
        this.factoryId
      )
    );
    this.submitErrors = [];
    this.clearSaved();
  }

  protected beforeDestroy(): void {
    this.clearSaved();
  }

  protected async performSubmit(): Promise<void> {
    this.clearSaved();
    if (!this.canManage) {
      return;
    }
    const items = this.rows
      .filter((row) => Number(row.percent) > 0)
      .map(
        (row) =>
          new FactoryProductDepositSetting(
            "",
            this.factoryId,
            row.productId,
            Number(row.percent)
          )
      );
    const saved = await this.fFactoryDataStore.saveProductDepositSettingsForFactory(
      {
        factoryId: this.factoryId,
        items,
      }
    );
    this.rebuildRows(saved);
    this.showSaved = true;
    this.fSavedTimer = setTimeout(() => {
      this.showSaved = false;
    }, 1800);
  }

  protected async created(): Promise<void> {
    try {
      const [items] = await Promise.all([
        this.fFactoryDataStore.loadProductDepositSettingsByFactoryId({
          factoryId: this.factoryId,
        }),
        this.fPlushieDataStore.loadProducts(),
      ]);
      this.rebuildRows(items);
    } catch (error) {
      this.loadErrors = this.fErrorConverterService.describeError(error);
    } finally {
      this.isLoading = false;
    }
  }

  private clearSaved(): void {
    clearTimeout(this.fSavedTimer);
    this.showSaved = false;
  }

  private compareProducts(firstId: string, secondId: string): number {
    const first = this.productLabel(firstId).toLowerCase();
    const second = this.productLabel(secondId).toLowerCase();
    return first < second
      ? -1
      : first > second
      ? 1
      : Number(firstId) - Number(secondId);
  }

  private rebuildRows(items: FactoryProductDepositSetting[]): void {
    this.rows = items
      .map((item) => ({
        key: item.id,
        productId: item.productId,
        percent: String(item.depositPercent),
        isSaved: true,
      }))
      .sort((first, second) =>
        this.compareProducts(first.productId, second.productId)
      );
    this.$nextTick(() => this.$validator.errors.clear());
    this.fConfirmedRows = this.rows.map((row) => ({ ...row }));
  }

  @Watch("rows", { deep: true })
  private onDraftChange(): void {
    if (this.hasChanges) {
      this.clearSaved();
    }
  }
}
